// src/services/membershipApplications.js
import { fetchWithAuth } from '../utils/fetchWithAuth';

const API_BASE = (import.meta?.env?.VITE_API_URL || '').replace(/\/+$/, '');
const USED_KEY = 'acceptedApps:used';

export const recordAutofillLocally = (id) => {
  try {
    const arr = JSON.parse(sessionStorage.getItem(USED_KEY) || '[]');
    if (!arr.includes(id)) {
      arr.push(id);
      sessionStorage.setItem(USED_KEY, JSON.stringify(arr));
    }
  } catch {}
};

export const removeAutofillLocally = (id) => {
  try {
    const arr = JSON.parse(sessionStorage.getItem(USED_KEY) || '[]');
    const next = arr.filter((x) => x !== id);
    sessionStorage.setItem(USED_KEY, JSON.stringify(next));
  } catch {}
};

/* ============================================================
 * Helpers
 * ============================================================ */
const ensurePdf = (file, maxBytes = 10 * 1024 * 1024) => {
  if (!file) throw new Error('Debes adjuntar un archivo PDF.');
  const isMimePdf = file.type === 'application/pdf';
  const isExtPdf = /\.pdf$/i.test(file.name || '');
  if (!isMimePdf && !isExtPdf) {
    throw new Error('Solo se permite formato PDF para la carta de recomendación.');
  }
  if (file.size > maxBytes) {
    throw new Error(`El archivo supera el límite (${Math.round(maxBytes / (1024 * 1024))} MB).`);
  }
};

const ensureJpgPng = (file, maxBytes = 10 * 1024 * 1024) => {
  if (!file) return;
  const ok =
    file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    /\.(png|jpg|jpeg)$/i.test(file.name || '');
  if (!ok) throw new Error('El logo debe ser JPG o PNG.');
  if (file.size > maxBytes) throw new Error('El logo es demasiado pesado.');
};

/* ============================================================
 * Listado / lectura / estado / pdf
 * ============================================================ */

/**
 * Lista postulaciones con filtros/paginación.
 * params: { status?, q?, page?, pageSize? }
 */
export const listMembershipApplications = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();

  const res = await fetchWithAuth(`/membership-applications${qs ? `?${qs}` : ''}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json(); // { ok, data, meta }
};

/* ============================================================
 * NUEVO: Búsqueda de ACEPTADAS para autocompletar
 * ============================================================ */
export const searchAcceptedMembershipApps = async (opts = {}) => {
  const { q = '', page = 1, pageSize = 10 } = opts || {};
  return listMembershipApplications({ status: 'ACCEPTED', q, page, pageSize });
};

/** Obtiene una postulación */
export const getMembershipApplication = async (id) => {
  const res = await fetchWithAuth(`/membership-applications/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json(); // { ok, data }
};

/**
 * Cambia el estado de una postulación.
 * status: 'RECEIVED' | 'IN_REVIEW' | 'ACCEPTED' | 'REJECTED'
 */
export const setMembershipApplicationStatus = async (id, status) => {
  const res = await fetchWithAuth(`/membership-applications/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/** Regenera el PDF en backend y re-sube a Cloudinary */
export const regenerateMembershipPdf = async (id) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/regenerate-pdf`,
    { method: 'POST' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/** Obtiene la URL del PDF de declaración jurada (ya generado) */
export const getDeclarationPdf = async (id) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/declaration-pdf`,
    { method: 'GET' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, pdf: { viewUrl, downloadUrl, secureUrl, originalName, ... } }
};

/** Regenera el PDF de declaración jurada en backend y re-sube a Cloudinary */
export const regenerateDeclarationPdf = async (id) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/regenerate-declaration-pdf`,
    { method: 'POST' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/**
 * (Opcional histórico) Crear manualmente en JSON.
 * OJO: tu controller actual espera multipart en POST /membership-applications.
 * Si quieres mantener JSON, usa el endpoint /membership-applications/manual.
 */
export const createMembershipApplication = async (payload) => {
  const res = await fetchWithAuth('/membership-applications/manual', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/** Lista de documentos generados (WELCOME / STATUTES) para una postulación aceptada */
export const listGeneratedDocs = async (id) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/generated-docs`,
    { method: 'GET' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  // { ok, data: GeneratedDocInfo[], docsGeneratedAt }
  return res.json();
};

/** Pide la URL de descarga de un documento generado por tipo ('WELCOME' | 'STATUTES') */
export const getGeneratedDocDownloadUrl = async (id, type) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/download/${encodeURIComponent(type)}`,
    { method: 'GET' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  // { ok, url, doc }
  return res.json();
};

export const getMembershipPdfTemplate = async (id) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/pdf-template`,
    { method: 'GET' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/** Actualiza el snapshot/answers (lo que editas en la plantilla) */
export const updateMembershipAnswers = async (id, answers) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/answers`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(answers),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data: app }
};

/* ============================================================
 * NUEVO: actualizar respuestas y regenerar PDF automáticamente
 * ============================================================ */
export const updateMembershipAnswersAndRegen = async (id, answers, autoRegen = false) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/answers?autoRegen=${autoRegen}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(answers),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }

  return res.json(); // { ok, data }
};

/* ============================================================
 * NUEVO: marcar autocompletar como usado (persistente)
 * ============================================================ */
/**
 * Marca una postulación como “completada” (usada para autocompletar).
 * Backend: PATCH /membership-applications/:id/autofill
 * Body: { used: boolean, by?: string }
 */
export const markApplicationAutofill = async (id, { used = true, by } = {}) => {
  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/autofill`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ used, by }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, data }
};

/* ============================================================
 * NUEVO: Carta de recomendación (solo PDF)
 * ============================================================ */

/**
 * Público (SIN auth): sube la carta de recomendación **solo PDF** por RUT.
 * Backend: POST /membership-applications/recommendation-letter
 * Body: multipart/form-data { rut: string, letter: File(PDF) }
 */
export const uploadRecommendationLetterByRut = async (rut, file, { signal } = {}) => {
  if (!API_BASE) throw new Error('Falta configurar VITE_API_URL para llamadas públicas.');
  if (!rut) throw new Error('Debes indicar el RUT.');
  ensurePdf(file);

  const fd = new FormData();
  fd.append('rut', rut);
  fd.append('letter', file, file.name || 'carta.pdf');

  const res = await fetch(`${API_BASE}/membership-applications/recommendation-letter`, {
    method: 'POST',
    body: fd,
    signal,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, ... }
};

/**
 * Con auth: adjunta un **PDF** a una postulación existente por ID.
 * Reutiliza tu endpoint actual de attachments (colgamos la carta en carpeta_tributaria).
 * Backend: POST /membership-applications/:id/attachments
 * Body: multipart/form-data { carpetaTributaria[]: File(PDF) }
 */
export const attachPdfToApplicationById = async (id, file) => {
  if (!id) throw new Error('Falta el ID de la postulación.');
  ensurePdf(file);

  const fd = new FormData();
  fd.append('carpetaTributaria[]', file, file.name || 'carta.pdf');

  const res = await fetchWithAuth(
    `/membership-applications/${encodeURIComponent(id)}/attachments`,
    { method: 'POST', body: fd }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, id, carpetaTributaria, logo }
};

/* ============================================================
 * (Opcional) Público multipart para crear la postulación
 * alineado a tu controller POST /membership-applications
 * ============================================================ */
/**
 * Crea una postulación con multipart (público, sin auth).
 * @param {{ payload: object, carpetaTributaria?: File[], logoAlta?: File|null, cartaRecomendacion?: File|null, folder?: string }} args
 */
export const publicCreateMembershipApplicationMultipart = async ({
  payload,
  carpetaTributaria = [],
  logoAlta = null,
  cartaRecomendacion = null,
  folder, // opcional: ?folder=memberships/applications
} = {}) => {
  if (!API_BASE) throw new Error('Falta configurar VITE_API_URL para llamadas públicas.');
  if (!payload) throw new Error('payload requerido');

  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  (carpetaTributaria || []).forEach((f) => {
    ensurePdf(f);
    fd.append('carpetaTributaria[]', f, f.name);
  });
  if (logoAlta) {
    ensureJpgPng(logoAlta);
    fd.append('logoAlta', logoAlta, logoAlta.name);
  }
  if (cartaRecomendacion) {
    ensurePdf(cartaRecomendacion);
    fd.append('cartaRecomendacion', cartaRecomendacion, cartaRecomendacion.name || 'carta.pdf');
  }

  const url = `${API_BASE}/membership-applications${
    folder ? `?folder=${encodeURIComponent(folder)}` : ''
  }`;
  const res = await fetch(url, { method: 'POST', body: fd });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `HTTP ${res.status}`);
  }
  return res.json(); // { ok, id, status, folderKey, files: { ... } }
};

/* ============================================================
 * NUEVO: Normalización de adjuntos (Cloudinary-friendly)
 * ============================================================ */

/** Campos típicos donde puede venir una URL (Cloudinary + custom) */
const URL_FIELDS = [
  'viewUrl',
  'secureUrl',
  'secure_url',
  'fileUrl',
  'downloadUrl',
  'url',
  'href',
  'link',
];
/** Campos para mostrar nombre legible si existe */
const NAME_FIELDS = [
  'name',
  'fileName',
  'filename',
  'originalName',
  'originalname',
  'original_filename',
  'publicId',
  'public_id',
  'key',
];

/** Resuelve rutas relativas contra la API o contra el origin actual */
const resolveMaybeRelativeUrl = (s = '') => {
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return API_BASE ? `${API_BASE}${s}` : `${window.location.origin}${s}`;
  if (/^(uploads|storage|files|assets)\//i.test(s)) {
    return API_BASE ? `${API_BASE}/${s}` : `/${s}`;
  }
  return s;
};

const fileNameFromUrl = (u = '') => {
  try {
    const p = new URL(u);
    const last = p.pathname.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(last) || u;
  } catch {
    const clean = u.split('?')[0].split('#')[0];
    return clean.split('/').pop() || u;
  }
};

const looksLikeFilePath = (s = '') => typeof s === 'string' && /[/.]\w{2,}/.test(s);

const collectFileEntries = (input, depth = 0, out = [], seen = new Set()) => {
  if (depth > 4 || input == null) return out;

  if (typeof input === 'string') {
    if (looksLikeFilePath(input)) {
      const href = resolveMaybeRelativeUrl(input);
      if (!seen.has(href)) {
        seen.add(href);
        out.push({ href, label: fileNameFromUrl(href) });
      }
    }
    return out;
  }

  if (Array.isArray(input)) {
    input.forEach((it) => collectFileEntries(it, depth + 1, out, seen));
    return out;
  }

  if (typeof input === 'object') {
    let href;
    for (const k of URL_FIELDS) {
      if (typeof input[k] === 'string' && looksLikeFilePath(input[k])) {
        href = resolveMaybeRelativeUrl(input[k]);
        break;
      }
    }
    if (!href) {
      for (const k in input) {
        const v = input[k];
        if (typeof v === 'string' && looksLikeFilePath(v)) {
          href = resolveMaybeRelativeUrl(v);
          break;
        }
      }
    }
    if (href && !seen.has(href)) {
      seen.add(href);
      let label;
      for (const k of NAME_FIELDS) {
        if (typeof input[k] === 'string' && input[k].trim()) {
          label = input[k];
          break;
        }
      }
      if (!label) label = fileNameFromUrl(href);
      out.push({ href, label });
    }

    for (const k in input) {
      if (input[k] && typeof input[k] === 'object')
        collectFileEntries(input[k], depth + 1, out, seen);
    }
  }
  return out;
};

/**
 * Extrae adjuntos (carta, carpeta tributaria, logo) desde múltiples posibles rutas:
 * - Cloudinary: { secure_url, url, original_filename, public_id, ... }
 * - Tu API:     { viewUrl, fileUrl, downloadUrl, name, ... }
 * - Estructuras: answers.adjuntos.*, files.*, attachments.*, etc.
 */
export const extractAttachmentEntries = (app = {}) => {
  const src = app || {};

  const cartaCandidates = [
    src.cartaRecomendacionFile && [src.cartaRecomendacionFile],
    src.recommendationLetterFiles,
    src.answers?.adjuntos?.carta_recomendacion,
    src.cartaRecomendacion,
    src.files?.cartaRecomendacion,
    src.files?.recommendationLetter,
    src.files?.letter,
    src.recommendationLetter,
    src.letter,
    src.letterFile,
    src.letterUrl,
    src.attachments?.cartaRecomendacion,
    src.attachments?.recommendationLetter,
  ].filter(Boolean);

  const carpetaCandidates = [
    src.carpetaTributariaFiles,
    src.answers?.adjuntos?.carpeta_tributaria,
    src.carpetaTributaria,
    src.files?.carpetaTributaria,
    src.attachments?.carpetaTributaria,
    src.carpetaTributariaUrls,
    src.carpetaTributariaLinks,
  ].filter(Boolean);

  const logoCandidates = [
    src.logoFiles,
    src.answers?.adjuntos?.logo_alta,
    src.logoAlta,
    src.files?.logoAlta,
    src.files?.logo,
    src.logo,
    src.logoUrl,
    src.attachments?.logo,
  ].filter(Boolean);

  return {
    letter: collectFileEntries(cartaCandidates),
    carpeta: collectFileEntries(carpetaCandidates),
    logo: collectFileEntries(logoCandidates),
  };
};

/**
 * Conveniencia: obtiene la postulación e incorpora attachments normalizados
 * en `data.attachmentsNormalized = { letter, carpeta, logo }`
 */
export const getMembershipApplicationNormalized = async (id) => {
  const j = await getMembershipApplication(id); // { ok, data }
  const data = j?.data || j;
  const attachmentsNormalized = extractAttachmentEntries(data);
  return { ...(j?.ok != null ? j : { ok: true }), data: { ...data, attachmentsNormalized } };
};

/**
 * Conveniencia: lista postulaciones e incorpora, para cada item, `attachmentsNormalized`.
 * Ojo: para listas grandes, puede ser pesado (recorre objetos). Úsalo si lo necesitas.
 */
export const listMembershipApplicationsNormalized = async (params = {}) => {
  const j = await listMembershipApplications(params); // { ok, data, meta }
  const data = Array.isArray(j?.data) ? j.data : Array.isArray(j) ? j : [];
  const mapped = data.map((item) => ({
    ...item,
    attachmentsNormalized: extractAttachmentEntries(item),
  }));
  return { ...(j?.ok != null ? j : { ok: true }), data: mapped, meta: j?.meta };
};
